import { useEffect, useState } from "react"
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material"
import ContentCopyIcon from "@mui/icons-material/ContentCopy"

type NewApiKeyDialogProps = {
  apiKeyValue: string | null
  onClose: () => void
}

async function copyToClipboard(value: string): Promise<void> {
  await navigator.clipboard.writeText(value)
}

export function NewApiKeyDialog({
  apiKeyValue,
  onClose,
}: NewApiKeyDialogProps) {
  const [copyStatus, setCopyStatus] = useState<"idle" | "success" | "error">(
    "idle"
  )

  useEffect(() => {
    setCopyStatus("idle")
  }, [apiKeyValue])

  const handleCopy = async () => {
    if (!apiKeyValue) {
      return
    }

    try {
      await copyToClipboard(apiKeyValue)
      setCopyStatus("success")
    } catch {
      setCopyStatus("error")
    }
  }

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      onClose={onClose}
      open={Boolean(apiKeyValue)}
    >
      <DialogTitle>New API Key</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {copyStatus === "success" ? (
            <Alert severity="success">API key copied to clipboard.</Alert>
          ) : copyStatus === "error" ? (
            <Alert severity="error">
              Unable to copy automatically. Copy the value manually.
            </Alert>
          ) : (
            <Alert severity="warning">
              Copy this API key now. It is only shown once.
            </Alert>
          )}

          <TextField
            fullWidth
            label="API key"
            value={apiKeyValue ?? ""}
            InputProps={{
              readOnly: true,
              endAdornment: (
                <InputAdornment position="end">
                  <Tooltip title="Copy API key">
                    <IconButton
                      aria-label="Copy API key"
                      edge="end"
                      onClick={() => void handleCopy()}
                    >
                      <ContentCopyIcon />
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              ),
            }}
            inputProps={{
              sx: {
                fontFamily: "monospace",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              },
            }}
          />

          <Typography color="textSecondary" variant="body2">
            Store this key somewhere safe before closing this dialog.
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="contained" onClick={onClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  )
}
